import { AssetDetailsTabOptions } from '../../components/asset-details-tabs/asset-details-tabs';
import { AnalysisArtifacts } from './asset-details-context';

enum AssetDetailsActionType {
  HeroScoreTabSelected = 'hero-score-tab-selected',
  SparkTabSelected = 'spark-tab-selected',
  ImageAnalysisTabSelected = 'image-analysis-tab-selected',
  SelectedArtifactChanged = 'selected-artifact-changed',
  SparkImagesTabSelected = 'spark-images-tab-selected',
  SelectedGeneratedImageChanged = 'selected-generated-image-changed',
  HideImagesNotHigherThanOriginalChanged = 'hide-images-not-higher-than-original-changed',
  OriginalAssetChanged = 'original-asset-changed',
}

interface AssetDetailsState {
  currentTab: AssetDetailsTabOptions;
  selectedArtifact: AnalysisArtifacts;
  selectedGeneratedImageId: string;
  /**
   * Whether the Spark Images tab hides generated images that do not score
   * higher than the original. Defaults to on.
   */
  hideImagesNotHigherThanOriginal: boolean;
}

const assetDetailsReducer = (
  previousState: AssetDetailsState,
  action: { type: AssetDetailsActionType; payload?: Partial<AssetDetailsState> & { originalAssetId?: string } },
) => {
  switch (action.type) {
    case AssetDetailsActionType.HeroScoreTabSelected:
      return {
        ...previousState,
        currentTab: AssetDetailsTabOptions.HeroScore,
        selectedArtifact: AnalysisArtifacts.OriginalImage,
        selectedGeneratedImageId: action.payload?.originalAssetId ?? previousState.selectedGeneratedImageId,
      };
    case AssetDetailsActionType.SparkTabSelected:
      return {
        ...previousState,
        currentTab: AssetDetailsTabOptions.SparkIdeas,
        selectedArtifact: AnalysisArtifacts.OriginalImage,
        // Reset to original image when switching away from Spark Images
        selectedGeneratedImageId: action.payload?.originalAssetId ?? previousState.selectedGeneratedImageId,
      };
    case AssetDetailsActionType.SparkImagesTabSelected:
      return {
        ...previousState,
        currentTab: AssetDetailsTabOptions.SparkImages,
        selectedArtifact: AnalysisArtifacts.OriginalImage,
        // Preserve selected generated image when switching to Spark Images tab
        selectedGeneratedImageId: previousState.selectedGeneratedImageId,
      };
    case AssetDetailsActionType.ImageAnalysisTabSelected:
      return {
        ...previousState,
        currentTab: AssetDetailsTabOptions.ImageAnalysis,
        selectedArtifact:
          previousState.currentTab === AssetDetailsTabOptions.SparkImages
            ? AnalysisArtifacts.OriginalImage
            : previousState.selectedArtifact,
        // Reset to original image when switching away from Spark Images
        selectedGeneratedImageId: action.payload?.originalAssetId ?? previousState.selectedGeneratedImageId,
      };
    case AssetDetailsActionType.HideImagesNotHigherThanOriginalChanged:
      return {
        ...previousState,
        hideImagesNotHigherThanOriginal:
          action.payload?.hideImagesNotHigherThanOriginal ?? previousState.hideImagesNotHigherThanOriginal,
      };
    case AssetDetailsActionType.SelectedArtifactChanged:
      return {
        ...previousState,
        selectedArtifact: action.payload?.selectedArtifact ?? previousState.selectedArtifact,
      };
    case AssetDetailsActionType.SelectedGeneratedImageChanged:
      return {
        ...previousState,
        selectedGeneratedImageId: action.payload?.selectedGeneratedImageId ?? previousState.selectedGeneratedImageId,
      };
    case AssetDetailsActionType.OriginalAssetChanged:
      // The page navigated to a different asset while staying mounted (the
      // router reuses the component when only the route param changes), so the
      // previewed selection and artifact belong to the previous subject.
      // Return the viewer to the new asset's original image and re-sync the
      // tab from the route's tab param (currentTab is reducer state, seeded
      // only at mount, so a navigation carrying ?tab= must push it in). The
      // hide toggle is a user preference and is left alone.
      return {
        ...previousState,
        selectedArtifact: AnalysisArtifacts.OriginalImage,
        selectedGeneratedImageId: action.payload?.originalAssetId ?? previousState.selectedGeneratedImageId,
        currentTab: action.payload?.currentTab ?? previousState.currentTab,
      };
    default:
      return previousState;
  }
};

export { assetDetailsReducer, AssetDetailsActionType };
export type { AssetDetailsState };
